// Hand-written, unlike its siblings: this derives phrases FROM the generated
// `doshas.ts` projection rather than being emitted by `pnpm build`.
//
// Why it exists (2026-08-25). Mangal Dosha's house set was retyped into 25
// authored surfaces, which drifted into five mutually inconsistent definitions —
// {1,2,4,7,8,12}, {1,4,7,8,12}, {1,4,7,8}, {2,4,7,8,12} and the governed
// {4,7,8,12}. Prose must consume the governed set, never restate it (rule 24).
//
// Source of truth: business/ishvaram/data/domain/astrology/doshas.yaml.

import { doshas } from "./doshas";

const EN_ORDINAL_SUFFIX = (house: number): string => {
  if (house >= 11 && house <= 13) return "th";
  return { 1: "st", 2: "nd", 3: "rd" }[house % 10] ?? "th";
};

// Devanagari ordinals are a language table, not a fact — the FACT is which of
// them the governed set selects.
const HI_ORDINALS = [
  "प्रथम",
  "द्वितीय",
  "तृतीय",
  "चतुर्थ",
  "पंचम",
  "षष्ठ",
  "सप्तम",
  "अष्टम",
  "नवम",
  "दशम",
  "एकादश",
  "द्वादश",
] as const;

const mangal = doshas.find((dosha) => dosha.id === "mangal_dosh");
if (!mangal?.trigger_houses?.length) {
  // Fail at module load, not at render: a silently empty phrase would publish
  // "Mars in the house from the Ascendant" to a seeker.
  throw new Error("doshaPhrases: mangal_dosh has no trigger_houses in the governed projection");
}

/** The governed Mangal Dosha trigger houses, ascending. BPHS 80.47. */
export const MANGAL_TRIGGER_HOUSES: readonly number[] = [...mangal.trigger_houses].sort(
  (a, b) => a - b
);

const joinEn = (parts: readonly string[]): string =>
  parts.length < 2 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")}, or ${parts.at(-1)}`;

const joinHi = (parts: readonly string[]): string =>
  parts.length < 2 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} या ${parts.at(-1)}`;

/** The trigger houses as a bare comma-joined list, for a label or table cell. */
export const MANGAL_HOUSE_NUMBERS = MANGAL_TRIGGER_HOUSES.join(", ");

/** The trigger houses as English ordinals — the tail of "Mars in the ... house". */
export const MANGAL_HOUSES_EN = joinEn(
  MANGAL_TRIGGER_HOUSES.map((house) => `${house}${EN_ORDINAL_SUFFIX(house)}`)
);

/** The trigger houses as Devanagari ordinals — the tail of "मंगल ... भाव में". */
export const MANGAL_HOUSES_HI = joinHi(
  MANGAL_TRIGGER_HOUSES.map((house) => HI_ORDINALS[house - 1] ?? String(house))
);

// The Ascendant is the whole reference frame. `dosha_mangal.py` discards the
// house table and reads the Ascendant-referenced house number, so naming the
// Moon or Venus here would be a false claim about what the product computes.
/** The full one-sentence definition, benefic clause included (BPHS 80.47). */
export const MANGAL_DEFINITION_EN =
  `Mangal Dosha (Manglik) is Mars in the ${MANGAL_HOUSES_EN} house from the Ascendant (Lagna), ` +
  `unaspected by and unassociated with any benefic`;

export const MANGAL_DEFINITION_HI =
  `मांगलिक दोष तब बनता है जब मंगल लग्न से ${MANGAL_HOUSES_HI} भाव में हो ` +
  `और किसी शुभ ग्रह की दृष्टि या युति उस पर न हो`;

/** How the engine reads the chart — one frame, stated once, reused everywhere. */
export const MANGAL_REFERENCE_FRAME_EN =
  "counted from the Ascendant (Lagna) alone, which is the reference the classical verse names";

export const MANGAL_REFERENCE_FRAME_HI =
  "गणना केवल लग्न से होती है — शास्त्रीय श्लोक यही आधार बताता है";
